import React from "react";
import { Link } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";
import { AdminGuard } from "@/components/admin/AdminGuard";
import { adminGet } from "@/lib/admin-api";

interface PostListItem {
  id: string;
  title: string;
  slug: string;
  status: string;
  source: string;
  excerpt: string;
  authorSlug: string;
  author: { name: string };
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
}

interface ListResponse {
  items: PostListItem[];
}

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  pending_review: "Pending review",
  scheduled: "Scheduled",
  published: "Published",
  archived: "Archived",
};

function StatusBadge({ status }: { status: string }) {
  const colour =
    status === "published"
      ? "text-emerald-600"
      : status === "pending_review"
        ? "text-amber-600"
        : status === "archived"
          ? "text-ink-3"
          : "text-ink-2";
  return (
    <span className={`font-mono text-[10px] uppercase tracking-widest ${colour}`}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

function VoicesAdminInner() {
  const [items, setItems] = React.useState<PostListItem[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [filter, setFilter] = React.useState<string>("");

  const load = React.useCallback(async () => {
    setError(null);
    try {
      const url = filter ? `/api/admin/voices/posts/list?status=${filter}` : "/api/admin/voices/posts/list";
      const res = await adminGet<ListResponse>(url);
      setItems(res.items);
    } catch (err) {
      setError(String(err));
    }
  }, [filter]);

  React.useEffect(() => {
    void load();
  }, [load]);

  return (
    <PageShell title="Voices · Admin" status="admin">
      <Seo title="Voices admin — The .person Foundation" description="" path="/admin/voices" noindex />
      <div className="mt-8">
        <div className="flex flex-wrap items-center gap-4 mb-8">
          <Link
            to="/admin/voices/new"
            className="bg-ink text-paper font-mono text-xs uppercase tracking-widest px-4 py-2.5 hover:bg-ink-2"
          >
            + New post
          </Link>
          <Link
            to="/admin/voices/queue"
            className="font-mono text-xs uppercase tracking-widest text-ink-2 underline hover:text-ink"
          >
            Review queue
          </Link>
          <Link
            to="/admin/connect"
            className="font-mono text-xs uppercase tracking-widest text-ink-2 underline hover:text-ink"
          >
            Workspace connections
          </Link>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="ml-auto border border-hairline bg-paper px-3 py-2 font-mono text-xs"
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="pending_review">Pending review</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {error && (
          <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 font-mono text-xs text-red-500 mb-6">
            {error}
          </div>
        )}

        {items === null && !error && (
          <p className="font-mono text-xs text-ink-3 uppercase tracking-widest">Loading…</p>
        )}

        {items && items.length === 0 && (
          <p className="font-serif text-base text-ink-2 leading-relaxed">
            No posts yet. <Link to="/admin/voices/new" className="text-claude underline">Create the first one →</Link>
          </p>
        )}

        {items && items.length > 0 && (
          <ul className="divide-y divide-hairline border-t border-b border-hairline">
            {items.map((post) => (
              <li key={post.id} className="py-4">
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-1">
                  <StatusBadge status={post.status} />
                  <span className="font-mono text-[10px] text-ink-3 uppercase tracking-widest">
                    {post.source}
                  </span>
                  <span className="font-mono text-[10px] text-ink-3">
                    {new Date(post.updatedAt).toLocaleString("en-GB")}
                  </span>
                  <span className="ml-auto font-mono text-xs text-ink-3">
                    by {post.author?.name ?? post.authorSlug}
                  </span>
                </div>
                <h2 className="font-serif text-lg text-ink mb-1">
                  <Link
                    to={`/admin/voices/${post.id}`}
                    className="hover:text-claude transition-colors"
                  >
                    {post.title || "(untitled)"}
                  </Link>
                </h2>
                {post.excerpt && (
                  <p className="font-serif text-sm text-ink-2 leading-relaxed line-clamp-2">
                    {post.excerpt}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </PageShell>
  );
}

export function VoicesAdmin() {
  return (
    <AdminGuard>
      <VoicesAdminInner />
    </AdminGuard>
  );
}
